import { Canvas as FabricCanvas, Image, Text, Group } from 'fabric';

/**
 * Canvas manager for Sport Trading Cards
 */
export default class STCCanvas {
    /**
     * Create a new canvas manager
     * 
     * @param {HTMLElement} container - Canvas container element
     */
    constructor(container) {
        this.container = container;
        this.currentSide = 'front';
        this.template = null;
        
        // Track elements by ID for reuse
        this.elementRefs = new Map();
        
        // Track elements by side for visibility management
        this.frontElements = new Set();
        this.backElements = new Set();
        
        // Current side's element data
        this.elements = [];
        
        this.initialize();
    }

    /**
     * Initialize canvas
     */
    initialize() {
        const width = 3099; // 63mm at 300dpi
        const height = 4329; // 88mm at 300dpi
    
        // Create main container that will hold both slab preview and canvas
        this.mainContainer = document.createElement('div');
        this.mainContainer.className = 'stc-canvas-main-container';
        this.container.appendChild(this.mainContainer);
    
        // Create slab preview container (initially hidden)
        this.slabPreviewContainer = document.createElement('div');
        this.slabPreviewContainer.className = 'stc-slab-preview-container';
        this.slabPreviewContainer.style.display = 'none';
        this.mainContainer.appendChild(this.slabPreviewContainer);
    
        // Create canvas wrapper with proper positioning
        this.wrapper = document.createElement('div');
        this.wrapper.className = 'stc-canvas-wrapper';
        this.mainContainer.appendChild(this.wrapper);
    
        // Create canvas element
        const canvasEl = document.createElement('canvas');
        this.wrapper.appendChild(canvasEl);
    
        // Initialize Fabric canvas with v6 options
        this.canvas = new FabricCanvas(canvasEl, {
            preserveObjectStacking: true,
            selection: false, 
            width,
            height, 
            fireRightClick: true,
            stopContextMenu: true,
            interactive: true,
            renderOnAddRemove: true
        });
    
        // Initialize properties for slab preview
        this.hasSlabPreview = false;
        this.slabPreviewHeight = 0;
    
        // Add event listeners for canvas resize
        window.addEventListener('resize', this.handleResize.bind(this));
    }

    /**
     * Set slab preview enabled state
     * 
     * @param {boolean} enabled - Whether the slab preview should be shown
     */
    setSlabPreviewEnabled(enabled) {

        this.hasSlabPreview = enabled;
        this.slabPreviewContainer.style.display = enabled ? 'block' : 'none';
        
        // Request recalculation of canvas scaling
        this.centerCanvas();
    }

    /**
     * Update slab preview content
     * 
     * @param {object} slabData - Slab configuration data
     * @param {string} side - Current side ('front' or 'back')
     */
    updateSlabPreview(slabData, side) {
        if (!slabData) return;

        // Clear previous content
        this.slabPreviewContainer.innerHTML = '';
        
        // Create preview based on side
        const previewHTML = this.generateSlabPreviewHTML(slabData, side);
        this.slabPreviewContainer.innerHTML = previewHTML;
        
        // Store current preview height for scaling calculations
        this.slabPreviewHeight = this.slabPreviewContainer.offsetHeight;
        
        // Make sure the preview container is visible
        this.slabPreviewContainer.style.display = 'flex';
        this.hasSlabPreview = true;
        
        // Update canvas scaling
        this.centerCanvas();
    }

    /**
     * Generate HTML for slab preview
     * 
     * @param {object} slabData - Slab configuration data
     * @param {string} side - Current side ('front' or 'back')
     * @returns {string} HTML for slab preview
     */
    generateSlabPreviewHTML(slabData, side) {
        if (side === 'front') {
            return `
                <div class="stc-slab-preview-content stc-slab-preview-front">
                    <div class="stc-slab-preview-body">
                        <div class="stc-slab-preview-row">
                            <div class="stc-slab-preview-field">${slabData.textField1 || 'Name'}</div>
                            <div class="stc-slab-preview-field">${slabData.textField4 || '#001'}</div>
                        </div>
                        <div class="stc-slab-preview-row">
                            <div class="stc-slab-preview-field">${slabData.textField2 || 'Company'}</div>
                            <div class="stc-slab-preview-field">${slabData.textField5 || 'GEM MT'}</div>
                        </div>
                        <div class="stc-slab-preview-row">
                            <div class="stc-slab-preview-field">${slabData.textField3 || 'Date'}</div>
                            <div class="stc-slab-preview-field">${slabData.textField6 || '10'}</div>
                        </div>
                    </div>
                </div>
            `;
        } else {
            // Back side preview
            const hasQR = slabData.backSide && slabData.backSide.qrEnabled && slabData.backSide.qrDataUrl;
            
            return `
                <div class="stc-slab-preview-content stc-slab-preview-back">
                    <div class="stc-slab-preview-body">
                        <div class="stc-slab-back-preview-content">
                            <div class="stc-slab-back-preview-left">
                                <div class="stc-slab-back-preview-text">CUSTOM CARD LAB</div>
                                <div class="stc-slab-back-preview-text">@CUSTOMCARDLAB</div>
                                <div class="stc-slab-back-preview-text">ROOKIE AUTO</div>
                            </div>
                            <div class="stc-slab-back-preview-middle">
                                <img src="/wp-content/plugins/octo-stc/public/assets/company_logo.webp" alt="Logo">
                            </div>
                            <div class="stc-slab-back-preview-right">
                                ${hasQR ? `<img src="${slabData.backSide.qrDataUrl}" alt="QR Code">` : ''}
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }
    }

    /**
     * Load template into canvas
     * 
     * @param {object} template - Template configuration
     */
    async loadTemplate(template) {
        try {
            this.template = template;
            
            // Load both sides initially
            await this.loadTemplateSide('front');
            await this.loadTemplateSide('back');

            // Show only elements for current side
            this.currentSide = 'front';
            this.showSideElements('front');
            
            // Center and render
            this.centerCanvas();
            this.canvas.requestRenderAll();
            
            // Notify that template is ready
            this.emit('templateReady', { side: this.currentSide });
            
        } catch (error) {
            console.error('Error loading template:', error);
            throw error;
        }
    }

    /**
     * Load elements for a specific side
     * 
     * @param {string} side - 'front' or 'back'
     */
    async loadTemplateSide(side) {
        const sideConfig = this.template[side];

        if (!sideConfig || !sideConfig.elements) throw new Error(`No configuration found for ${side} side`);
    
        // Get ordered elements
        const elements = this.getOrderedElements(sideConfig.elements);
        const sideElements = side != 'back' ? this.frontElements : this.backElements;

        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];

            const fabricObj = await this.createOrUpdateElement(element, i);

            if( !fabricObj ) continue;
            sideElements.add(element.id);
        }
    }

    /**
     * Create or update an element
     * 
     * @param {object} element - Element data
     * @param {number} zIndex - Z-index for the element
     * @returns {object} Fabric object
     */
    async createOrUpdateElement(element, zIndex) {
        // Check if element already exists
        let fabricObj = this.elementRefs.get(element.id);
        
        // If not, create it
        if (!fabricObj) {
            try {
                switch (element.type) {
                    case 'static_image':
                        fabricObj = await this.createStaticImage(element);
                        break;
                    case 'masked_image':
                        fabricObj = await this.createMaskedImage(element);
                        break;
                    case 'editable_text':
                    case 'card_number':
                        fabricObj = await this.createEditableText(element);
                        break;
                    case 'editable_image':
                    case 'section_placeholder':
                        fabricObj = await this.createEditableImage(element);
                        break;
                    default:
                        console.warn(`Unknown element type: ${element.type}`);
                        return null;
                }
            } catch (error) {
                console.error('Error creating element:', element, error);
                return null;
            }
        }

        if (!fabricObj) return null;

        // Set/update element properties
        fabricObj.set({
            name: element.id,
            ...element.data,
            selectable: false,
            hasControls: false,
            lockMovementX: true,
            lockMovementY: true,
            originX: 'center',
            originY: 'center',
            centeredScaling: true,
            zIndex: zIndex,
            evented: element.data.selectable || false,
        });

        if (!this.elementRefs.has(element.id)) {
            this.canvas.add(fabricObj);
            this.elementRefs.set(element.id, fabricObj);
        }

        return fabricObj;
    }

    /**
     * Get elements ordered by z-index
     * 
     * @param {object} elements - Elements from template
     * @returns {Array} Ordered elements
     */
    getOrderedElements(elements) {
        return Object.entries(elements)
            .map(([id, element]) => ({
                id,
                ...element
            }))
            .sort((a, b) => 
                (a.data.zIndex || 0) - (b.data.zIndex || 0)
            );
    }

    /**
     * Create a static image element
     * 
     * @param {object} element - Element data
     * @returns {Promise<Image>} Fabric image object
     */
    async createStaticImage(element) {
        return await Image.fromURL(element.data.src);
    }

    /**
     * Create a masked image element
     * 
     * @param {object} element - Element data
     * @returns {Promise<Image>} Fabric image object
     */
    async createMaskedImage(element) {

        try {
            const maskElement = this.getElementData(element.data.maskId);

            const [mainImage, mask] = await Promise.all([
                Image.fromURL(element.data.src),
                Image.fromURL(maskElement.data.src)
            ]);

            mask.set({
                name: maskElement.id,
                ...maskElement.data,
                selectable: false,
                hasControls: false,
                originX: 'center',
                originY: 'center',
                absolutePositioned: true,
            });

            // Apply mask using clipPath
            mainImage.clipPath = mask;

            return mainImage;
        } catch (error) {
            console.error('Error creating masked image:', error);
            throw error;
        }
    }

    /**
     * Create an editable text element
     * 
     * @param {object} element - Element data
     * @returns {Text} Fabric text object
     */
    async createEditableText(element) {
        // Preserve fill if it exists, otherwise use color
        // This ensures colors from edit mode (stored in fill) are not overwritten
        element.data.fill = element.data.fill || element.data.color;
        return new Text(element.data.text || '', {
            ...element.data,
            selectable: false,
            editable: false,
            objectCaching: true
        });
    }

    /**
     * Create an editable image element
     * 
     * @param {object} element - Element data
     * @returns {Promise<Image>} Fabric image object
     */
    async createEditableImage(element) {
        element.data.src = element.data.src || '/wp-content/plugins/octo-stc/public/assets/placeholder.svg';
        return this.createStaticImage(element);
    }

    /**
     * Create a group of elements
     * 
     * @param {string} groupId - Group ID
     * @param {Array} objects - Objects to include in group
     * @param {object} options - Group options
     * @returns {Group} Fabric group object
     */
    createGroup(groupId, objects = [], options = {}) {
        const group = new Group(objects, {
            name: groupId,
            selectable: false,
            hasControls: false,
            ...options
        });
        
        this.canvas.add(group);
        this.elementRefs.set(groupId, group);
        
        return group;
    }

    /**
     * Add an object to a group
     * 
     * @param {string} groupId - Group ID
     * @param {object} object - Object to add
     * @returns {boolean} Success indicator
     */
    addToGroup(groupId, object) {
        const group = this.elementRefs.get(groupId);
        if (!group || !group.addWithUpdate) return false;
        
        group.addWithUpdate(object);
        return true;
    }

    /**
     * Remove an object from a group
     * 
     * @param {string} groupId - Group ID
     * @param {string} objectId - Object ID
     * @returns {boolean} Success indicator
     */
    removeFromGroup(groupId, objectId) {
        const group = this.elementRefs.get(groupId);
        if (!group) return false;
        
        const object = this.elementRefs.get(objectId);
        if (!object) return false;
        
        group.remove(object);
        return true;
    }

    /**
     * Center canvas in wrapper
     */
    centerCanvas() {
        const containerWidth = this.container.clientWidth;
        const containerHeight = this.container.clientHeight;
        const canvasWidth = this.canvas.width;
        const canvasHeight = this.canvas.height;
        
        // Calculate slab preview height if visible
        const slabPreviewHeight = this.hasSlabPreview ? 
            this.slabPreviewContainer.offsetHeight : 0;
        
        // Calculate scale based on available space including slab preview height
        const totalContentHeight = canvasHeight + slabPreviewHeight;
        
        const scale = Math.min(
            (containerWidth / canvasWidth) * 0.9,
            (containerHeight / totalContentHeight) * 0.9
        );
        
        // Apply transform to main container instead of wrapper
        this.mainContainer.style.transform = `scale(${scale})`;
        this.mainContainer.style.transformOrigin = 'center center';
        
        // Set consistent width on both components
        this.wrapper.style.width = canvasWidth + 'px';
        this.wrapper.style.height = canvasHeight + 'px';
        
        // Ensure the slab preview has the same width as the canvas
        if (this.hasSlabPreview) {
            // this.slabPreviewContainer.style.width = `${canvasWidth}px`;
        }
    }

    /**
     * Handle window resize
     */
    handleResize() {
        // Debounce resize handling
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
            this.centerCanvas();
        }, 250);
    }

    /**
     * Change the current side
     * 
     * @param {string} side - 'front' or 'back'
     */
    changeSide(side) {
        if (side === this.currentSide) return;
        
        this.currentSide = side;
        
        this.showSideElements(side);
        
        this.canvas.requestRenderAll();
        
        if (this.hasSlabPreview) this.emit('slabSideChange', { side });
        
        this.emit('templateReady', { side });
    }

    /**
     * Show elements for the specified side and hide others
     * 
     * @param {string} side - 'front' or 'back'
     */
    showSideElements(side) {
        // Get element sets
        const showElements = side === 'front' ? this.frontElements : this.backElements;
        const hideElements = side === 'front' ? this.backElements : this.frontElements;

        console.log('Show elements:', showElements);
        console.log('Hide elements:', hideElements);
        console.log(side);
        
        // Hide elements from other side
        hideElements.forEach(id => {
            const element = this.elementRefs.get(id);
            if (element) {
                element.set('visible', false);
            }
        });
        
        // Show elements for current side
        showElements.forEach(id => {
            const element = this.elementRefs.get(id);
            if (element) {
                element.set('visible', true);
            }
        });
    }

    /**
     * Get an element by ID
     * 
     * @param {string} id - Element ID
     * @returns {object} Fabric object
     */
    getElement(id) {
        return this.elementRefs.get(id);
    }

    /**
     * Get element data from template
     * 
     * @param {string} id - Element ID
     * @returns {object} Element data
     */
    getElementData(id) {
        const sideConfig = this.template[this.currentSide];
        if (!sideConfig || !sideConfig.elements || !sideConfig.elements[id]) {
            return null;
        }
        
        return {
            id,
            ...sideConfig.elements[id]
        };
    }

    /**
     * Check if an element belongs to current side
     * 
     * @param {string} id - Element ID
     * @returns {boolean} True if element belongs to current side
     */
    isElementOnCurrentSide(id) {
        return this.currentSide === 'front' ? 
            this.frontElements.has(id) : 
            this.backElements.has(id);
    }

    /**
     * Update an element's properties
     * 
     * @param {string} id - Element ID
     * @param {object} properties - New properties
     */
    updateElement(id, properties) {
        const element = this.getElement(id);
        if (element) {
            element.set(properties);
            this.canvas.requestRenderAll();
        }
    }

    /**
     * Update all elements for a side with the latest step data
     * @param {string} side - 'front' or 'back'
     * @param {object} stepsData - Data for all steps on this side
     */
    updateSideWithData(side, stepsData) {
        // Switch to the side if needed
        if (this.currentSide !== side) this.changeSide(side);
        
    }

    /**
     * Clean up resources
     */
    destroy() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        this.canvas.dispose();
        this.wrapper.remove();
        this.elementRefs.clear();
        this.elements = [];
    }

    /**
     * Emit an event
     * 
     * @param {string} eventName - Event name
     * @param {object} detail - Event detail
     */
    emit(eventName, detail = {}) {
        const event = new CustomEvent(`stc:canvas:${eventName}`, { detail });
        this.container.dispatchEvent(event);
    }
    
    /**
     * Listen for an event
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    on(eventName, callback) {
        this.container.addEventListener(`stc:canvas:${eventName}`, callback);
    }
    
    /**
     * Remove event listener
     * 
     * @param {string} eventName - Event name
     * @param {function} callback - Event callback
     */
    off(eventName, callback) {
        this.container.removeEventListener(`stc:canvas:${eventName}`, callback);
    }

}